import NewMessageModal from '@/components/Modals/NewMessageModal';
import { Button, Input, Form, message, InputRef, Pagination, Tag, Spin, Space, Image, Popconfirm, Badge } from 'antd';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { EnterOutlined, PlusOutlined, UploadOutlined, FilterOutlined } from '@ant-design/icons';
import { ChatRoomStatus, IMessage, IMessageQuery, IMessageRoom } from '@/types/app';
import { appendContent, downloadFileFromURL, matchValueToLabel, openNotification } from '@/utils';
import { enumOptions } from '@/common/constants/enums';
import Ckeditor from '@/components/common/ckeditor';
import { useDispatch, useSelector } from '@/stores';
import { messageActions } from '@/stores/messages';
import { messageRoomActions } from '@/stores/messageRooms';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ROUTE_PATH } from '@/common/constants/routes';
import dayjs from 'dayjs';
import { dateTimeFormat, dateTimeFormatNotSecond, pageSizeOptions } from '@/common/constants/format';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ReactComponent as SearchIcon } from '@/assets/images/new-icons/search-icon.svg';
import { ReactComponent as UploadIcon } from '@/assets/images/new-icons/upload-icon.svg';
import CustomUpload from './customUpload';
import FileUploadComponent from './customUpload';
import { UploadFile } from 'antd/lib/upload';
import FilterMessageModal from '@/components/Modals/FilterMessageModal';

const initialState = {
  id: '',
  userId: '',
  userName: '',
  department: '',
  subject: '',
  message: '',
};

const SendMessage = () => {
  const { constants } = useSelector((state) => state.app);
  const { userInfo } = useSelector((state) => state.auth);
  const {
    data: messages,
    loading: messageLoading,
    pagination: messagePagination,
    detail: messageDetail,
  } = useSelector((state) => state.messages);
  const { data: messageRooms, loading, pagination, detail: messageRoomDetail, filter } = useSelector((state) => state.messageRooms);

  const [fileName, setFileName] = useState('');
  // const [attachmentUpload, setAttachmentUpload] = useState<File>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [replyText, setReplyText] = useState<string>('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const handleOpenModal = (e: boolean) => {
    setIsModalOpen(e);
  };

  const getSuccessFileList = () => {
    return fileList
      .filter((file) => file?.status === 'done')
      .map((file) => (file.originFileObj && file.originFileObj instanceof File ? file.originFileObj : null))
      .filter((file) => file !== null);
  };

  const transformBeforeSubmit = (e: any) => {
    return {
      ...e,
      chatRoomID: id,
      attachments: getSuccessFileList(),
    };
  };

  // const handleReplySubmit = async (e: { message: string }) => {
  const handleReplySubmit = async (e: any) => {
    form.resetFields();
    const result = transformBeforeSubmit(e);
    // if (e?.message) appendContent(e);
    await dispatch(messageActions.createMessage(result))
      .unwrap()
      .then((resp) => {
        dispatch(messageRoomActions.sortChatRoomsByNewestMessage(resp?.data?.data));

        const element = document.getElementById('message-content');
        if (element) element.scrollTop = element.scrollHeight;
        dispatch(messageActions.getMoreMessages({ chatRoomID: id }));
      });
    if (messageRoomDetail?.status === 'close') {
      await handleChangeStatusMessageRoom('active', false);
      dispatch(messageRoomActions.getMessageRoom({ id }));
      onChangeFilter({status: 'active'});
    }
    setFileList([]);
    // setAttachmentUpload(undefined);
    setFileName('');
    setReplyText('');
  };

  useEffect(
    function () {
      if (id) {
        dispatch(messageRoomActions.getMessageRoom({ id })).unwrap().then((res) => {
          onChangeFilter({status: res.data.data.status});
        });
        dispatch(messageActions.getMoreMessages({ chatRoomID: id }));
        dispatch(
          messageRoomActions.markAsSeenMessageRoom({
            id,
          }),
        ).then(() => {
          dispatch(messageRoomActions.getMessageRoomStatistics({status: 'active'}));
          dispatch(messageRoomActions.getMessageRooms({...filter}));
        });
      }
    },
    [id],
  );

  useEffect(() => {
    form.resetFields();
  }, [messageRoomDetail]);

  const fetchMoreMessages = () => {
    if (messagePagination.page < messagePagination.totalPage) {
      dispatch(
        messageActions.getMoreMessages({
          chatRoomID: id,
          page: messagePagination.page + 1,
        }),
      );
      setLoadingIntervalMessages(false);
    }
  };

  const fetchMoreMessageRooms = () => {
    if (pagination.page < pagination.totalPage) {
      dispatch(
        messageRoomActions.getMoreMessageRooms({
          page: pagination.page + 1,
        }),
      );
      setLoadingInterval(false);
    }
  };

  // const handleChange = function (e: ChangeEvent<HTMLInputElement>) {
  //   e.preventDefault();
  //   if (e.target.files && e.target.files[0]) {
  //     handleFile(e.target.files);
  //   }
  // };

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onChangeFilter({ keyword: value });
  };


  const onChangeFilter = (value: IMessageQuery) => {
    dispatch(
      messageRoomActions.setFilter({
        ...filter,
        ...value,
      }),
    );
  };

  const handleSubmitFilter = (values: IMessageQuery) => {
    let newValues: IMessageQuery = { ...values};
    onChangeFilter(newValues);
    setIsFilterModalOpen(false);
    navigate(ROUTE_PATH.MESSAGES);
  };
  const handleChangeStatusMessageRoom = async (status: ChatRoomStatus, notification: boolean = true)=>{
    await dispatch(
      messageRoomActions.changeStatusMessageRoom({
        id,
        status,
      }),
    ).unwrap().then(()=>{
      // if(notification) openNotification("success", "Change status successfully!");
    });
    await dispatch(
      messageRoomActions.setFilter({
        ...filter,
      }),
    );
  }

  useEffect(() => {
    return () => {
      dispatch(messageRoomActions.resetFilter());
    };
  }, []);

  useEffect(() => {
    dispatch(messageRoomActions.getMessageRooms(filter));
  }, [filter]);

  const [loadingInterval, setLoadingInterval] = useState<boolean>(false);
  const [loadingIntervalMessages, setLoadingIntervalMessages] = useState<boolean>(false);

  useEffect(function () {
    let interval;
    interval = setInterval(()=>{
      dispatch(messageRoomActions.getMessageRooms({...filter, ...pagination, keyword: searchValue}));
      if (id) {
        dispatch(messageRoomActions.getMessageRoom({ id })).unwrap().then((res) => {
          onChangeFilter({status: res.data.data.status});
        });
        dispatch(messageActions.getMoreMessages({ chatRoomID: id }));
        dispatch(
          messageRoomActions.markAsSeenMessageRoom({
            id,
          }),
        ).then(() => {
          dispatch(messageRoomActions.getMessageRoomStatistics({ status: 'active' }));
          dispatch(messageRoomActions.getMessageRooms({ ...filter }));
        });
      }
      setLoadingInterval(true);
    }, 5000);
    return ()=>{
      clearInterval(interval);
    }
  }, [pagination, filter, searchValue, id]);

  return (
    <div id="send-message" className="container-fluid">
      <Spin tip="Loading" size="large" spinning={loading[messageRoomActions.getMessageRooms.typePrefix]&&!loadingInterval}>
        <div className="d-flex justify-content-between flex-column flex-md-row" style={{}}>
          <div className="card-header" style={{ marginBottom: '20px', marginTop: '20px', padding: 0 }}>
            <div className="" style={{ color: '#1E1D1D', fontSize: '24px', lineHeight: '32px', fontWeight: 700 }}>
              Message
            </div>
          </div>
          <div className="d-flex align-items-center flex-column flex-md-row">
            {/* <Button className="d-flex align-items-center mr-2" type="primary" ghost onClick={() => setIsFilterModalOpen(true)}>
              <FilterOutlined />
              Filter
            </Button> */}
            <FilterMessageModal
              open={isFilterModalOpen}
              // destroyOnClose={true}
              onCancel={() => setIsFilterModalOpen(false)}
              onSubmit={handleSubmitFilter}
              status={messageRoomDetail?.status}
            />
            <Button
              className="d-flex align-items-center"
              style={{ marginBottom: '20px', marginTop: '20px' }}
              type="primary"
              onClick={() => handleOpenModal(true)}
            >
              <PlusOutlined />
              Add New
            </Button>
          </div>
        </div>
        <div className="bg-white rounded border" style={{ height: "calc(100vh - 150px), overflow: 'auto" }}>
          <div className="card-body row p-0 d-flex flex-column flex-xl-row flex-lg-row">
            <div className="col-lg-4 p-0 message-list rounded">
              <div className="sticky-search">
                <div className="sticky-search d-flex border-bottom" style={{ padding: '15px 24px' }}>
                  <Input
                    placeholder="Search..."
                    allowClear
                    prefix={<SearchIcon />}
                    className="custom-search-input"
                    value={searchValue}
                    onChange={handleChangeValue}
                  />
                </div>
              </div>
              {messageRooms && messageRooms.length ? (
                <InfiniteScroll
                  // scrollableTarget="scrollableDiv"
                  // below props only if you need pull down functionality

                  // className="d-flex flex-column-reverse"
                  hasMore={(messageRooms?.length || 0) < pagination?.total}
                  dataLength={messageRooms?.length || 0}
                  // height={650}
                  style={{ height: 'calc(100vh - 230px)' }}
                  // pullDownToRefreshThreshold={1}
                  // inverse={true}
                  // scrollableTarget="message-content"
                  next={fetchMoreMessageRooms}
                  // endMessage={
                  //   <p style={{ textAlign: "center" }}>
                  //     <b>You have seen it all</b>
                  //   </p>
                  // }
                  loader={<Spin tip="Loading" size="small" spinning={loading[messageRoomActions.getMoreMessageRooms.typePrefix]&&!loadingInterval}></Spin>}
                  className="p-3"
                >
                  {messageRooms?.map((item) => (
                    <Link
                      to={`${ROUTE_PATH.MESSAGES}/${item.id}`}
                      className=""
                      // onClick={() => getMessageDetail(item.id)}
                      key={item.id}
                    >
                      <div className={`message-hover p-3 rounded ${item.id === id ? 'message-selected border' : ''}`}>
                        <div className="d-flex justify-content-between align-items-center">
                          {/* <b className="text-blue">{item.email}</b> */}
                          {/* <span className="small">{item.sentTime}</span> */}
                        </div>
                        <div className="d-flex align-items-center">
                          <b className="mr-2 message-subject" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 600 }}>
                            {item.subject}
                          </b>
                          {parseInt(item?.totalMessagesNotSeen || '') > 0 ? (
                            // <span
                            //   style={{
                            //     borderRadius: '50%',
                            //     backgroundColor: 'red',
                            //     color: 'white',
                            //     fontSize: '12px',
                            //     fontWeight: 'bold',
                            //   }}
                            //   className="px-1"
                            // >
                            //   {item?.totalMessagesNotSeen}
                            // </span>
                            <Badge count={item?.totalMessagesNotSeen}></Badge>
                          ) : null}
                        </div>

                        <div
                          className="d-flex align-items-center"
                          style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 500, color: '#424141' }}
                        >
                          <p className="mb-0 mr-1">
                            {/* Department:  */}
                            {matchValueToLabel(constants?.departments || [], item.department || '')}
                          </p>
                          <div>|</div>

                          <div className="ml-1">
                            {item?.participant?.forename && item?.participant?.surname
                              ? `${item.participant.forename} ${item.participant.surname}`
                              : item?.participant?.username || item?.participant?.email}
                          </div>
                        </div>
                        {item?.latestMessage?.message ? (
                          <div
                            style={{
                              color: '#8A8988',
                              fontSize: '12px',
                              lineHeight: '18px',
                              fontWeight: 600,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Last message: {item?.latestMessage?.message}
                          </div>
                        ) : null}
                      </div>
                    </Link>
                  ))}
                </InfiniteScroll>
              ) : (
                <span className="p-4">No message</span>
              )}
            </div>
            <div className="col-lg-8 shadow-sm bg-white message-content-wrapper p-0 border-left rounded-right">
              {messageRoomDetail?.id && id ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center border-bottom message-content-container">
                    <div className="flex-grow-1">
                      <div className="col-lg-8">
                        <div className="" style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}>
                          <div style={{ marginLeft: '5px' }}>{messageRoomDetail.subject}</div>
                        </div>
                      </div>

                      <div className="d-flex" style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 500 }}>
                        {/* <b>Department: </b> */}
                        <div className="mr-1" style={{ marginLeft: '12px' }}>
                          {matchValueToLabel(constants?.departments || [], messageRoomDetail.department || '')}
                        </div>
                        <div>|</div>
                        {messageRoomDetail?.participant && messageRoomDetail?.participant?.id !== '' && (
                          <div className="ml-1">
                            {/* {messageRoomDetail.participant?.forename} {messageRoomDetail.participant?.surname} */}
                            {messageRoomDetail?.participant?.name ||
                              messageRoomDetail?.participant?.username ||
                              messageRoomDetail?.participant?.email}
                          </div>
                        )}
                      </div>
                    </div>
                    <Popconfirm
                      title={messageRoomDetail?.status === 'close' ? 'Active Conversation' : 'Close Conversation'}
                      description="Are you sure you want to proceed?"
                      onConfirm={async function () {
                        await handleChangeStatusMessageRoom(messageRoomDetail?.status === 'close' ? 'active' : 'close');
                        navigate(ROUTE_PATH.MESSAGES);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      {messageRoomDetail?.status === 'close' ? (
                        <Button danger={false} className="font-weight-bold color-primary">
                          Active Conversation
                        </Button>
                      ) : userInfo?.role === 'company' ? (
                        <Button danger={true} className="font-weight-bold color-primary">
                          Close Conversation
                        </Button>
                      ) : null}
                    </Popconfirm>
                  </div>

                  <div className="col-md-12 p-0">
                    <div
                      id="scrollableDiv"
                      style={{
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                      }}
                      className="p-3 border-bottom"
                    >
                      <InfiniteScroll
                        // scrollableTarget="scrollableDiv"
                        // below props only if you need pull down functionality

                        className="d-flex flex-column-reverse pl-2 pr-2"
                        hasMore={(messages?.length || 0) < messagePagination?.total}
                        dataLength={messages?.length || 0}
                        // height={'80vh'}
                        style={{ height: 'calc(100vh - 370px)' }}
                        pullDownToRefreshThreshold={1}
                        inverse={true}
                        // scrollableTarget="message-content"
                        next={fetchMoreMessages}
                        // endMessage={
                        //   <p style={{ textAlign: "center" }}>
                        //     <b>You have seen it all</b>
                        //   </p>
                        // }
                        loader={
                          <Spin tip="Loading" size="small" spinning={messageLoading[messageActions.getMoreMessages.typePrefix]&&!loadingIntervalMessages}></Spin>
                        }
                      >
                        {messages?.map((item) => (
                          // <div key={item.id} className={item.sender?.id === userInfo?.id ? 'text-end' : 'text-align-left'}>
                          <span
                            key={item.id}
                            style={{
                              textAlign: item.sender?.id === userInfo?.id ? 'right' : 'left',
                              // width: item.sender?.id === userInfo?.id ? '500px' : '500px',
                              display: 'inline-block',
                              // width: '50%',
                              // width: 'fit-content',
                              maxWidth: '50%',
                              alignSelf: item.sender?.id === userInfo?.id ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <div className="">
                              {item.sender?.id === userInfo?.id ? (
                                ''
                              ) : (
                                <small style={{ fontSize: '12px', lineHeight: '18px', fontWeight: 700, color: '#1E1D1D' }}>
                                  {/* {item?.sender?.email} */}

                                  {/* {item?.sender?.name}{' '} */}
                                  {item?.sender?.name && item?.sender?.name.trim() !== ''
                                    ? item.sender.name
                                    : item?.sender?.username || item?.sender?.email}
                                </small>
                              )}
                              <small style={{ marginLeft: '5px', fontSize: '12px', lineHeight: '18px', fontWeight: 400, color: '#8A8988' }}>
                                {dayjs(item?.createdAt).format(dateTimeFormatNotSecond)}
                              </small>
                            </div>

                            {item?.message ? (
                              <div style={{ marginTop: '10px' }}>
                                {item?.message?.split('\n').map(
                                  (item2, index) =>
                                    item2.trim() !== '' && ( // Kiểm tra item2 không phải là chuỗi rỗng
                                      <span
                                        key={index} // Luôn thêm key khi lặp qua một danh sách
                                        style={{
                                          border: '0.5px solid #AFADAC',
                                          borderRadius: '8px',
                                          padding: '8px 12px',
                                          marginBottom: '10px',
                                          backgroundColor: '#F5F2F0',
                                          color: '#1E1D1D',
                                          fontSize: '12px',
                                          lineHeight: '18px',
                                          fontWeight: 400,
                                          display: 'inline-block',
                                          width: 'fit-content',
                                        }}
                                      >
                                        {item2}
                                      </span>
                                    ),
                                )}
                              </div>
                            ) : null}

                            {item?.attachments?.map((attachment, index) => (
                              <div key={index} style={{ marginTop: '10px' }}>
                                {/(\.jpg|\.jpeg|\.png|\.gif)$/i.test(attachment.fileURL) ? (
                                  <img
                                    src={attachment.fileURL}
                                    alt={attachment?.metadata?.name || 'Attachment'}
                                    style={{
                                      maxWidth: '100%',
                                      borderRadius: '8px',
                                      marginBottom: '5px',
                                    }}
                                  />
                                ) : (
                                  <Tag
                                    style={{
                                      width: '100%',
                                      backgroundColor: item.sender?.id === userInfo?.id ? '#225EE5' : '#FBFAF8',
                                      color: item.sender?.id === userInfo?.id ? 'white' : '#1E1D1D',
                                    }}
                                  >
                                    <b
                                      className="cursor-pointer"
                                      style={{
                                        width: '100%',
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        paddingTop: 4,
                                      }}
                                      onClick={() => downloadFileFromURL(attachment.fileURL || '', attachment.metadata?.name)}
                                    >
                                      {attachment.metadata?.name}
                                    </b>
                                  </Tag>
                                )}
                              </div>
                            ))}
                          </span>
                        ))}
                      </InfiniteScroll>
                    </div>
                  </div>
                  <div className="col-md-12 p-2" style={{ alignItems: 'center' }}>
                    <Form form={form} onFinish={handleReplySubmit} className="p-3 d-flex align-items-center">
                      <div className="d-flex" style={{ flex: 1 }}>
                        <div className="d-flex" style={{ justifyContent: 'center', alignItems: 'center', marginRight: '5px' }}>
                          <CustomUpload condition="send" fileList={fileList} setFileList={setFileList} />
                        </div>
                        <div style={{ flex: 1 }}>
                          {' '}
                          <Form.Item name="message" style={{ width: '100%', marginBottom: '0px' }}>
                            <Input.TextArea
                              rows={3}
                              placeholder="Reply here..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onKeyDown={(e: any) => {
                                if (e?.keyCode == 10 || e?.keyCode == 13) {
                                  if (!e?.shiftKey) {
                                    handleReplySubmit({ message: replyText });
                                  }
                                }
                              }}
                              maxLength={500}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </div>
                      </div>

                      <Button
                        disabled={
                          loading[messageActions.createMessage.typePrefix] || (replyText.trim() === '' && getSuccessFileList().length <= 0)
                        }
                        htmlType="submit"
                        className="float-right p-3 ml-2 justify-content-center d-flex align-items-center"
                        style={{
                          flexShrink: 0,
                          color:
                            loading[messageActions.createMessage.typePrefix] ||
                            (replyText.trim() === '' && getSuccessFileList().length <= 0)
                              ? '#FDFCFB'
                              : 'white',

                          fontSize: '14px',
                          lineHeight: '20px',
                          fontWeight: 600,
                          backgroundColor:
                            loading[messageActions.createMessage.typePrefix] ||
                            (replyText.trim() === '' && getSuccessFileList().length <= 0)
                              ? '#225EE51a'
                              : '#225EE5',
                        }}
                      >
                        Send
                      </Button>
                    </Form>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <NewMessageModal
          isOpen={isModalOpen}
          setIsOpen={(e: boolean) => handleOpenModal(e)}
          hideTo={userInfo?.role === 'client'}
          role={userInfo?.role}
        />
      </Spin>
    </div>
  );
};

export default SendMessage;
